// ============================================================
// IE ENGINE — TERROR FRAME v2 (the house frame, forged)
//
// Extracted from Shatterfield.js (July 3, 2026) so any IE surface
// can dress a panel with one drop-in line — Workshop, Forge, Codex,
// Pack Shop, and eventually Pier/Casino at their session.
//
//   <div style={{ position: "relative", ... }}>
//     <TerrorFrame />
//     ...panel content...
//   </div>
//
// Requirements on the host panel:
//   • position: relative (the frame is absolutely positioned inside it)
//   • the panel itself must NOT scroll — if content scrolls, wrap the
//     content in an inner overflow region and keep the frame on the
//     outer shell (see Shatterfield's SoulModal for the pattern),
//     otherwise the frame scrolls away with the content.
//
// v1 → v2 (GILDED OBSIDIAN material pass, July 5, 2026):
//   The hairlines are now METAL — 2px gradient edge strips with a
//   bright core so light appears to run along the frame (Material
//   Law 2: no 1px hairline borders). Thorned corners carry a dark
//   under-stroke beneath the bright stroke (forged depth), the
//   crown gem gained a specular facet + soft glow, and the whole
//   frame breathes a faint drop-shadow. Anatomy, API, zIndex, and
//   pointerEvents are UNCHANGED.
//
// Anatomy: metal edge strips + inner hairline echo, four thorned
// corners, a crown finial (blood gem set in gold) top-center, and
// a quiet echo drop at the base.
// Pure view, zero state, pointerEvents:none — it can never eat a click.
//
// Defaults are the house golds (the literal hex of GM.inkGold /
// GM.inkGoldBright) so the engine file has zero theme imports;
// pass `color`/`bright`/`blood` to re-skin per surface.
// ============================================================

export default function TerrorFrame({ color = "#c8963c", bright = "#e8b84b", blood = "#8b2038", inset = 7 }) {
  const corners = [
    { top: inset - 4, left: inset - 4,  sx: 1,  sy: 1  },
    { top: inset - 4, right: inset - 4, sx: -1, sy: 1  },
    { bottom: inset - 4, left: inset - 4,  sx: 1,  sy: -1 },
    { bottom: inset - 4, right: inset - 4, sx: -1, sy: -1 },
  ];
  // metal edge — light runs along the strip (bright core, dark falloff)
  const metalH = `linear-gradient(90deg, transparent, ${color}00 4%, #a06a1e 14%, ${bright} 38%, #f5d878 50%, ${bright} 62%, #a06a1e 86%, ${color}00 96%, transparent)`;
  const metalV = `linear-gradient(180deg, transparent, ${color}00 4%, #a06a1e 14%, ${bright} 38%, #f5d878 50%, ${bright} 62%, #a06a1e 86%, ${color}00 96%, transparent)`;
  const cornerGap = 30; // strips stop short of the thorned corners
  return (
    <div style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 6, filter: `drop-shadow(0 0 6px ${color}33)` }}>
      {/* metal edge strips — the forged border */}
      <div style={{ position: "absolute", top: inset, left: cornerGap, right: cornerGap, height: 2, background: metalH, opacity: 0.9 }} />
      <div style={{ position: "absolute", bottom: inset, left: cornerGap, right: cornerGap, height: 2, background: metalH, opacity: 0.75 }} />
      <div style={{ position: "absolute", left: inset, top: cornerGap, bottom: cornerGap, width: 2, background: metalV, opacity: 0.82 }} />
      <div style={{ position: "absolute", right: inset, top: cornerGap, bottom: cornerGap, width: 2, background: metalV, opacity: 0.82 }} />
      {/* inner hairline echo */}
      <div style={{ position: "absolute", inset: inset + 4, border: `1px solid ${color}22`, borderRadius: 1 }} />
      {/* thorned corners — dark under-stroke beneath the bright = forged depth */}
      {corners.map((c, q) => (
        <svg key={q} width="34" height="34" viewBox="0 0 34 34" style={{
          position: "absolute",
          top: c.top, bottom: c.bottom, left: c.left, right: c.right,
          transform: `scale(${c.sx}, ${c.sy})`,
        }}>
          <path d="M 2 32 L 2 10 Q 2 2 10 2 L 32 2" fill="none" stroke="#6b4310" strokeWidth="2.6" strokeOpacity="0.85" />
          <path d="M 2 32 L 2 10 Q 2 2 10 2 L 32 2" fill="none" stroke={bright} strokeWidth="1.3" strokeOpacity="0.95" />
          <path d="M 2 30 L 2 10 Q 2 3.5 9 3.5 L 30 3.5" fill="none" stroke="#f5d878" strokeWidth="0.5" strokeOpacity="0.7" />
          <path d="M 2 20 Q 9 18 11 11 Q 18 9 20 2" fill="none" stroke={color} strokeWidth="1" opacity="0.75" />
          <path d="M 7 7 L 15 10 L 10 15 Z" fill={bright} opacity="0.9" />
          <path d="M 7 7 L 15 10 L 11 11 Z" fill="#f5d878" opacity="0.65" />
          <path d="M 22 2 L 26 5 L 22 8" fill="none" stroke={color} strokeWidth="1" opacity="0.6" />
          <path d="M 2 22 L 5 26 L 8 22" fill="none" stroke={color} strokeWidth="1" opacity="0.6" />
        </svg>
      ))}
      {/* crown finial — a blood gem set in forged gold */}
      <svg width="58" height="15" viewBox="0 0 58 15" style={{ position: "absolute", top: inset - 7, left: "50%", transform: "translateX(-50%)", filter: `drop-shadow(0 0 4px ${blood}88)` }}>
        <path d="M 0 7.5 H 20 M 38 7.5 H 58" stroke="#6b4310" strokeWidth="2" opacity="0.7" />
        <path d="M 0 7.5 H 20 M 38 7.5 H 58" stroke={bright} strokeWidth="0.9" opacity="0.85" />
        <path d="M 20 7.5 L 24 5 M 38 7.5 L 34 5" stroke={color} strokeWidth="0.8" opacity="0.55" />
        <path d="M 29 1 L 35.5 7.5 L 29 14 L 22.5 7.5 Z" fill="#0d0813" stroke={bright} strokeWidth="1.2" />
        <path d="M 29 1 L 35.5 7.5 L 29 14 L 22.5 7.5 Z" fill="none" stroke="#f5d878" strokeWidth="0.4" opacity="0.6" />
        <path d="M 29 4 L 32.5 7.5 L 29 11 L 25.5 7.5 Z" fill={blood} opacity="0.95" />
        <path d="M 29 4 L 32.5 7.5 L 29 7.5 Z" fill="#e05555" opacity="0.65" />
        <path d="M 27.2 5.8 L 28.4 5 L 28 6.6 Z" fill="#ffd9d9" opacity="0.75" />
      </svg>
      {/* base drop — quiet echo of the crown */}
      <svg width="34" height="11" viewBox="0 0 34 11" style={{ position: "absolute", bottom: inset - 5, left: "50%", transform: "translateX(-50%)" }}>
        <path d="M 0 5.5 H 11 M 23 5.5 H 34" stroke="#6b4310" strokeWidth="1.8" opacity="0.6" />
        <path d="M 0 5.5 H 11 M 23 5.5 H 34" stroke={bright} strokeWidth="0.8" opacity="0.6" />
        <path d="M 17 1.5 L 21 5.5 L 17 9.5 L 13 5.5 Z" fill="#0d0813" stroke={color} strokeWidth="1" opacity="0.85" />
        <path d="M 17 3.2 L 19.4 5.5 L 17 7.8 L 14.6 5.5 Z" fill={color} opacity="0.5" />
      </svg>
    </div>
  );
}
